import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { Task } from '@lit/task';

import { pageMetadata } from '@flakiness/server/common/pageMetadata.js';
import { Stats } from '@flakiness/server/common/stats/stats.js';
import { createFakeReportCommit } from '@flakiness/server/common/wireReport.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { ContextType } from '@lit/context';
import { api } from './api.js';
import { AttachmentUrlBuilder } from './artifact.js';
import { linkStyles, pageStyles } from './components/cssstyles.js';
import { ProjectContext } from './components/project-context.js';
import { consume, contexts } from './contexts.js';
import { FeatureReportViewerState } from './features/feature-report-viewer.js';
import { Linkifier } from './linkifier.js';
import { PageProjectRuns } from './page-project-runs.js';
import { projectPlatformUrl } from './platformUrl.js';
import { RouteConfig, URLState } from './router.js';
import { tasks } from './tasks.js';
import { URLOptions } from './urlOptions.js';

const pageSchema = {
  query: URLOptions.query,
  testTimeline: URLOptions.testTimeline,
  testAttempt: URLOptions.testAttempt,
  plane: URLOptions.plane,
  timelineSplit: URLOptions.timelineSplit,
  page: URLOptions.page,
  pageSize: URLOptions.pageSize,
  sortName: URLOptions.sortName,
  sortDirection: URLOptions.sortDirection,
  historyType: URLOptions.historyType,
};

@customElement('page-run')
export class PageRun extends LitElement {

  static routes(): RouteConfig[] {
    return [{
      path: [
        '/:org/:project/run/:reportId',
        '/:org/:project/run/:reportId/',
        '/:org/:project/run/:reportId/test/:testId',
        '/:org/:project/run/:reportId/test/:testId/',
      ],
      render: (groups) => ProjectContext.render({
        orgSlug: groups?.org,
        projectSlug: groups?.project,
      }, html`
        <page-run
          .reportId=${groups?.reportId ? parseInt(groups.reportId) : undefined}
          .testId=${groups?.testId}
        ></page-run>
      `),
    }];
  }

  static url(options: FeatureReportViewerState & {
    orgSlug: string, 
    projectSlug: string,
    reportId: Stats.RunId,
    // Required so that no link forgets it; undefined before a snapshot loads.
    snapshotId: WireTypes.DataSnapshotId | undefined,
  }) {
    const pathname = ['', options.orgSlug, options.projectSlug, 'run', options.reportId];
    if (options.testId)
      pathname.push('test', options.testId);
    const params = URLState.toSearchParams(pageSchema, {
      query: options.query?.trim() ? options.query : undefined,
      plane: options.plane,
      timelineSplit: options.timelineSplit,
      testAttempt: options.testAttempt,
      page: options.page,
      pageSize: options.pageSize,
      sortDirection: options.sortDirection,
      sortName: options.sortName,
      testTimeline: options.testTimeline,
      historyType: options.historyType,
    });
    return projectPlatformUrl(pathname.join('/'), params, options.snapshotId);
  }

  static linkifier(project: WireTypes.Project, snapshotId: WireTypes.DataSnapshotId | undefined): Linkifier<WireTypes.WellformedRun>  {
    return (run) => PageRun.url({
      orgSlug: project.org.orgSlug,
      projectSlug: project.projectSlug,
      reportId: run.runId,
      snapshotId,
    });
  }

  private _urlState = new URLState(this, pageSchema);

  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;
  @consume({ context: contexts.user, subscribe: true }) private _user?: ContextType<typeof contexts.user>;
  @consume({ context: contexts.router, subscribe: true }) private _router?: ContextType<typeof contexts.router>;
  @consume({ context: contexts.projectReferences, subscribe: true }) private _branches?: ContextType<typeof contexts.projectReferences>;

  @property({ attribute: false }) reportId?: Stats.RunId;
  @property({ attribute: false }) testId?: Stats.TestId;

  private _reportTask = new Task(this, {
    args: () => [
      this._project, this.reportId,
    ] as const,
    task: async ([project, reportId], { signal }) => {
      CHECK(project && reportId);
      const json = await api.run.json.GET({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        reportId: reportId,
      }, { signal });
      CHECK(json);
      return json;
    }
  });

  private _attachmentUrlBuilderTask = new Task(this, {
    args: () => [this._project, this.reportId] as const,
    task: async ([project, reportId], { signal }) => {
      CHECK(project && reportId);
      const orgSlug = project.org.orgSlug;
      const projectSlug = project.projectSlug;
      return ((attachmentId: string, isPlaywrightTrace?: boolean) => {
        const options = { orgSlug, projectSlug, reportId, attachmentId };
        if (isPlaywrightTrace)
          return api.run.playwrightTrace.GET.prepare(options).url.toString();
        return api.run.attachment.GET.prepare(options).url.toString();
      }) satisfies AttachmentUrlBuilder;
    }
  });

  private _commitTask = new Task(this, {
    args: () => [this._project, this._snapshotId, this._reportTask.value] as const,
    task: async ([project, snapshotId, report], { signal }) => {
      CHECK(report && project && snapshotId);
      const commit = await api.project.getCommit.GET({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
        head: report.commitId,
      }, { signal });
      return commit ?? createFakeReportCommit(report);
    }
  });

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this._project, this.reportId, this._reportTask.value] as const,
    metadata: ([project, reportId, report]) => project && reportId !== undefined ? pageMetadata({
      orgSlug: project.org.orgSlug,
      projectSlug: project.projectSlug,
      section: report?.title ? `Run #${reportId}: ${report.title}` : `Run #${reportId}`,
    }) : undefined,
  });

  override render() {
    const project = this._project;
    const reportId = this.reportId;
    if (!project || !reportId)
      return nothing;

    return html`
      <project-header
        .orgSlug=${project.org.orgSlug}
        .projectSlug=${project.projectSlug}
        .submenu=${"runs"}
      ></project-header>

      <project-body wide>
        ${this._reportTask.render({
          pending: () => html`<report-viewer-stub></report-viewer-stub>`,
          error: () => html`<h3>Error Loading Report</h3>`,
          complete: report => html`
            <feature-report-viewer
              .historyHead=${report.commitId}
              .reportId=${reportId}
              .report=${report}
              .commit=${this._commitTask.value}
              .attachmentURLBuilder=${this._attachmentUrlBuilderTask.value}
              .state=${{
                timelineSplit: this._urlState.state.timelineSplit,
                page: this._urlState.state.page,
                pageSize: this._urlState.state.pageSize,
                plane: this._urlState.state.plane,
                query: this._urlState.state.query,
                sortDirection: this._urlState.state.sortDirection,
                sortName: this._urlState.state.sortName,
                testAttempt: this._urlState.state.testAttempt,
                testId: this.testId && this._urlState.state.testTimeline ? this.testId : undefined,
                testTimeline: this._urlState.state.testTimeline,
                historyType: this._urlState.state.historyType,
              } satisfies FeatureReportViewerState}
              .linkifier=${(options: FeatureReportViewerState) => PageRun.url({
                orgSlug: project.org.orgSlug,
                projectSlug: project.projectSlug,
                reportId: reportId,
                snapshotId: this._snapshotId,
                ...options,
              })}
            >
              <sl-dropdown slot=title>
                <sl-icon-button slot="trigger" name=gear>
                </sl-icon-button>
                <sl-menu>
                  <sl-menu-item @click=${() => {
                    const url = api.run.json.GET.prepare({
                      orgSlug: project.org.orgSlug,
                      projectSlug: project.projectSlug,
                      reportId: reportId,
                    }).url.toString();
                    window.open(url, '_blank');
                  }}>
                    <sl-icon slot=prefix name=braces></sl-icon>
                    View raw JSON
                  </sl-menu-item>
                  ${this._project?.access === 'editor' ? html`
                    <sl-divider></sl-divider>
                    <sl-menu-item class=delete-btn @click=${async () => {
                      if (!confirm('Are you sure you want to delete this run?'))
                        return;
                      await api.run.deleteRun.POST({
                        orgSlug: project.org.orgSlug,
                        projectSlug: project.projectSlug,
                        runId: reportId,
                      });
                      await this._router?.navigate(PageProjectRuns.url({
                        orgSlug: project.org.orgSlug,
                        projectSlug: project.projectSlug,
                        snapshotId: this._snapshotId,
                      }));
                    }}>
                      <sl-icon slot=prefix name=trash></sl-icon>
                      <span>Delete</span>
                    </sl-menu-item>
                  ` : nothing}
                </sl-menu>
              </sl-dropdown>
            </feature-report-viewer>
          `
        })}
      </project-body>
      <app-footer></app-footer>
    `;
  }

  static styles = [linkStyles, pageStyles, css`
    .delete-btn * {
      color: var(--sl-color-danger-600);
    }
  `]
}
